var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jwtDecode } from "jwt-decode";
import { isGaGatewayConfigured, postAttribution } from "@/api/attribution/attribution";
import { hasConsent } from "@/services/initCookiebot/consent";
import MPStorage from "@/services/LocalStorage";
import { customKeys } from "@/services/LocalStorage/LocalStorage";
import { isRealPartnerId } from "@/utilities/isRealPartnerId";
import { buildAttributionPayload, getGaClientId, getGclid } from "./googleAttribution";
/**
 * Captures the browser-only Google identifiers (GA4 client ID, Google click
 * ID) at account creation and hands them to ga-gateway, so a purchase the
 * backend charges days later can still be attributed to the campaign that
 * brought the customer.
 *
 * Sent through flushPendingAccountAttribution, once the access token belongs
 * to the real partner account (the backend derives the account from the
 * token's org_name claim; nothing identifying is sent).
 *
 * Fire-and-forget: never throws and must never block or delay signup. Makes
 * no request at all when
 *  - no ga-gateway URL is configured for this environment,
 *  - statistics consent is denied (checked explicitly via hasConsent - cookie
 *    presence proves nothing, _ga may come from the marketing site), or
 *  - neither identifier is present (organic visitor before cookies, etc.).
 */
export var sendAccountAttribution = function () { return __awaiter(void 0, void 0, Promise, function () {
    var statistics, marketing, payload, _a, error_1;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                _c.trys.push([0, 3, , 4]);
                if (!isGaGatewayConfigured)
                    return [2 /*return*/];
                statistics = hasConsent("statistics");
                if (!statistics)
                    return [2 /*return*/];
                marketing = hasConsent("marketing");
                _a = buildAttributionPayload;
                _b = {};
                return [4 /*yield*/, getGaClientId()];
            case 1:
                payload = _a.apply(void 0, [(_b.gaClientId = _c.sent(), _b.gclid = getGclid(), _b), { statistics: statistics, marketing: marketing }]);
                if (!payload)
                    return [2 /*return*/];
                return [4 /*yield*/, postAttribution(payload)];
            case 2:
                _c.sent();
                return [3 /*break*/, 4];
            case 3:
                error_1 = _c.sent();
                // Logged and otherwise ignored: attribution must never affect signup.
                console.error("[attribution] Failed to store account attribution", error_1);
                return [3 /*break*/, 4];
            case 4: return [2 /*return*/];
        }
    });
}); };
// Local, not session, storage: registration clears the auth cache and
// re-authenticates, so the marker has to survive that round trip.
export var markAccountAttributionPending = function (userId) {
    if (!userId)
        return;
    MPStorage.saveToLocalStorage(customKeys.ATTRIBUTION_PENDING_FOR_USER, userId);
};
var isRealAccountToken = function (token) {
    try {
        return isRealPartnerId(jwtDecode(token).org_name);
    }
    catch (_a) {
        return false;
    }
};
export var flushPendingAccountAttribution = function () { return __awaiter(void 0, void 0, Promise, function () {
    var pendingForUser, _a, token, userId, error_2;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                _b.trys.push([0, 2, , 3]);
                pendingForUser = MPStorage.getFromLocalStorage(customKeys.ATTRIBUTION_PENDING_FOR_USER);
                if (!pendingForUser)
                    return [2 /*return*/];
                _a = MPStorage.requestCacheAuthenticate(), token = _a.token, userId = _a.userId;
                if (!token || userId !== pendingForUser)
                    return [2 /*return*/];
                // The backend keys the row on the token's organisation, so sending under
                // the shared signup placeholder would attribute the wrong account.
                if (!isRealAccountToken(token))
                    return [2 /*return*/];
                MPStorage.removeFromLS(customKeys.ATTRIBUTION_PENDING_FOR_USER);
                return [4 /*yield*/, sendAccountAttribution()];
            case 1:
                _b.sent();
                return [3 /*break*/, 3];
            case 2:
                error_2 = _b.sent();
                console.error("[attribution] Failed to flush account attribution", error_2);
                return [3 /*break*/, 3];
            case 3: return [2 /*return*/];
        }
    });
}); };
//# sourceMappingURL=data:application/json;base64,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